/**
 *
 * @module cardPaymentMethodResponse
 *
 */

import Check from 'check-types';
import Services from './../services/entry';
import PaymentMethodResponse from './paymentMethodResponse';

const PaymentCardType = Services.Commerce.PaymentCardType;

const InvalidArgumentException = Services.Exception.InvalidArgumentException;

const {
    boolean,
    nonEmptyObject,
    number,
    nonEmptyString
} = Check.assert;

/**
 *
 * @class CardPaymentMethodResponse
 * @desc Response containing information on a card payment method.
 * @note The billingAddress, isDefault, paymentMethodId, walletId and type parameters are listed here but not being
 * type-checked because the type-checking is being left to the super class, SDK.Commerce.PaymentMethodResponse.
 *
 */
export default class CardPaymentMethodResponse extends PaymentMethodResponse {

    /**
     *
     * @param {Object} options
     * @param {SDK.Services.Commerce.BillingAddress} options.billingAddress
     * @param {Boolean} options.isDefault
     * @param {String} options.paymentMethodId
     * @param {String} options.walletId
     * @param {SDK.Commerce.PaymentMethodType} options.type
     * @param {String} [options.alternateName=undefined]
     * @param {String} options.brand
     * @param {String} options.cardType=PaymentCardType.CREDIT
     * @param {String} options.country
     * @param {Number} options.expiryMonth
     * @param {Number} options.expiryYear
     * @param {String} options.firstSix
     * @param {Boolean} options.isCorporate
     * @param {String} options.issuer
     * @param {String} options.lastFour
     * @param {String} options.ownerFullName
     * @throws {InvalidArgumentException}
     *
     */
    constructor(options) {

        const errorMessage = {
            ctor: 'CardPaymentMethodResponse(options)',
            get options() {
                return `${this.ctor} options should be a non-empty Object`;
            },
            get alternateName() {
                return `${this.ctor} options.alternateName should be a String with length`;
            },
            get brand() {
                return `${this.ctor} options.brand should be a String with length`;
            },
            get cardType() {
                return `${this.ctor} options.cardType should be a String with length`;
            },
            get country() {
                return `${this.ctor} options.country should be a String with length`;
            },
            get expiryMonth() {
                return `${this.ctor} options.expiryMonth should be a Number`;
            },
            get expiryYear() {
                return `${this.ctor} options.expiryYear should be a Number`;
            },
            get firstSix() {
                return `${this.ctor} options.firstSix should be a String with length`;
            },
            get isCorporate() {
                return `${this.ctor} options.isCorporate should be a Boolean`;
            },
            get issuer() {
                return `${this.ctor} options.issuer should be a String with length`;
            },
            get lastFour() {
                return `${this.ctor} options.lastFour should be a String with length`;
            },
            get ownerFullName() {
                return `${this.ctor} options.ownerFullName should be a String with length`;
            }
        };

        nonEmptyObject(options, errorMessage.options, InvalidArgumentException);

        const { alternateName, brand, cardType = PaymentCardType.CREDIT, country, expiryMonth } = options;
        const { expiryYear, firstSix, isCorporate, issuer, lastFour, ownerFullName } = options;

        if (Check.assigned(alternateName)) {
            nonEmptyString(alternateName, errorMessage.alternateName, InvalidArgumentException);
        }

        nonEmptyString(brand, errorMessage.brand, InvalidArgumentException);
        nonEmptyString(cardType, errorMessage.cardType, InvalidArgumentException);
        nonEmptyString(country, errorMessage.country, InvalidArgumentException);
        number(expiryMonth, errorMessage.expiryMonth, InvalidArgumentException);
        number(expiryYear, errorMessage.expiryYear, InvalidArgumentException);
        nonEmptyString(firstSix, errorMessage.firstSix, InvalidArgumentException);
        boolean(isCorporate, errorMessage.isCorporate, InvalidArgumentException);
        nonEmptyString(issuer, errorMessage.issuer, InvalidArgumentException);
        nonEmptyString(lastFour, errorMessage.lastFour, InvalidArgumentException);
        nonEmptyString(ownerFullName, errorMessage.ownerFullName, InvalidArgumentException);

        super(options);

        /**
         *
         * @access public
         * @type {String|undefined}
         *
         */
        this.alternateName = alternateName;

        /**
         *
         * @access public
         * @type {String}
         *
         */
        this.brand = brand;

        /**
         *
         * @access public
         * @type {SDK.Services.Commerce.PaymentCardType}
         *
         */
        this.cardType = PaymentCardType[cardType];

        /**
         *
         * @access public
         * @type {String}
         *
         */
        this.country = country;

        /**
         *
         * @access public
         * @type {Number}
         *
         */
        this.expiryMonth = expiryMonth;

        /**
         *
         * @access public
         * @type {Number}
         *
         */
        this.expiryYear = expiryYear;

        /**
         *
         * @access public
         * @type {String}
         *
         */
        this.firstSix = firstSix;

        /**
         *
         * @access public
         * @type {Boolean}
         *
         */
        this.isCorporate = isCorporate;

        /**
         *
         * @access public
         * @type {String}
         *
         */
        this.issuer = issuer;

        /**
         *
         * @access public
         * @type {String}
         *
         */
        this.lastFour = lastFour;

        /**
         *
         * @access public
         * @type {String}
         *
         */
        this.ownerFullName = ownerFullName;

    }

}



// WEBPACK FOOTER //
// ./src/commerce/cardPaymentMethodResponse.js